<template>
    <div class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <div class="flex items-center gap-3">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                    Avaliar Saque
                </h3>
                <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ cashout.created_at }}
                </span>
            </div>
            <div class="flex items-center gap-2">
                <router-link
                    v-if="cashout"
                    :to="{
                        name: 'finnancial.cashout-details',
                        params: { cashout_id: cashout.id },
                    }"
                    target="_blank"
                    class="inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-xs text-indigo-600 transition-colors hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10"
                >
                    #{{ cashout.id }}
                    <ArrowTopRightOnSquareIcon class="h-3 w-3" aria-hidden="true" />
                </router-link>
                <router-link
                    v-if="cashout"
                    :to="{
                        name: 'user.audit.commissions',
                        params: { user_id: cashout.user_id },
                    }"
                    target="_blank"
                    class="inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-xs text-indigo-600 transition-colors hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10"
                >
                    #USER
                    <ArrowTopRightOnSquareIcon class="h-3 w-3" aria-hidden="true" />
                </router-link>
                <cashout-status :status="cashout.status" size="sm" />
                <Menu as="div" class="relative">
                    <MenuButton
                        class="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                    >
                        <EllipsisVerticalIcon class="h-4 w-4" />
                    </MenuButton>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            class="absolute right-0 z-10 mt-1 w-40 origin-top-right rounded-md border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-600 dark:bg-gray-700"
                        >
                            <MenuItem v-slot="{ active }">
                                <button
                                    type="button"
                                    class="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium"
                                    :class="active
                                        ? 'bg-gray-50 text-gray-900 dark:bg-gray-600 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-300'"
                                    @click="toggleAdvanced"
                                >
                                    {{ showAdvanced ? "Ocultar investigação" : "Investigação avançada" }}
                                </button>
                            </MenuItem>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>
        </div>

        <!-- Tags & Audit warnings -->
        <div
            v-if="cashoutTagsCount > 0 || (userAudits && userAudits.length > 0)"
            class="border-b border-gray-100 px-5 py-2.5 dark:border-gray-700/50"
        >
            <div class="flex items-center justify-between">
                <cashout-tags :cashout="cashout" @loaded="cashoutTagsCount = $event" />
                <div v-if="userAudits && userAudits.length > 0" class="flex items-center gap-2">
                    <router-link
                        v-for="userAudit in userAudits"
                        :key="userAudit.id"
                        :to="{
                            name: 'audit.interactions',
                            params: { audit_id: userAudit.id },
                        }"
                        target="_blank"
                        class="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 transition-colors hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20 dark:hover:bg-red-500/20"
                    >
                        <ExclamationCircleIcon class="h-3.5 w-3.5" />
                        Auditoria {{ userAudit.status === "closed" ? "finalizada" : "ativa" }}
                    </router-link>
                </div>
            </div>
        </div>
        <cashout-tags v-else :cashout="cashout" @loaded="cashoutTagsCount = $event" class="hidden" />

        <!-- Values -->
        <div class="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 dark:divide-gray-700/50 dark:border-gray-700/50" v-if="cashout">
            <div class="px-5 py-3">
                <div class="text-xs text-gray-500 dark:text-gray-400">Valor Solicitado</div>
                <div class="mt-0.5 font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ $root.formatCurrency(cashout.amount) }}
                </div>
            </div>
            <div class="px-5 py-3">
                <div class="text-xs text-gray-500 dark:text-gray-400">Taxa Aplicada</div>
                <div class="mt-0.5 font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ $root.formatCurrency(cashout.fee) }}
                </div>
            </div>
            <div class="px-5 py-3">
                <div class="text-xs text-gray-500 dark:text-gray-400">Valor a Receber</div>
                <div class="mt-0.5 font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {{ $root.formatCurrency(cashout.net_value) }}
                </div>
            </div>
        </div>

        <!-- Advanced investigation (collapsible) -->
        <div v-if="showAdvanced" class="border-b border-gray-100 dark:border-gray-700/50">
            <div class="flex items-center gap-1.5 overflow-x-auto px-5 py-2.5">
                <button
                    v-for="tab in investigationTabs"
                    :key="tab.id"
                    type="button"
                    class="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                    :class="tabButtonClass(tab)"
                    @click="activeTab = tab.id"
                >
                    <span>{{ tab.label }}</span>
                    <span
                        v-if="tab.badge"
                        class="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                        :class="tabBadgeClass(tab.priority)"
                    >
                        {{ tab.badge }}
                    </span>
                </button>
                <button
                    type="button"
                    class="ml-auto flex-shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                    @click="toggleAdvanced"
                >
                    <XMarkIcon class="h-4 w-4" />
                </button>
            </div>

            <div class="border-t border-gray-100 p-5 dark:border-gray-700/50">
                <section v-if="activeTab === 'system'" class="space-y-4">
                    <system-analysis
                        :analysis="audit?.analysis"
                        @data-loaded="updateSystemAnalysisSummary"
                    />
                </section>

                <section v-else-if="activeTab === 'payments'" class="space-y-4">
                    <bin-usage-card
                        :cashout="cashout"
                        @data-loaded="updateBinSummary"
                    />
                    <payment-method-card
                        :cashout="cashout"
                        @data-loaded="updatePaymentMethodSummary"
                    />
                </section>

                <section v-else-if="activeTab === 'commissions'" class="space-y-4">
                    <div
                        v-if="audit && audit.commission_types"
                        class="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    >
                        <h4 class="text-sm font-medium text-gray-900 dark:text-white">
                            Distribuição de Comissões
                        </h4>
                        <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            Concentração por origem para identificar padrões suspeitos.
                        </p>
                        <div class="mt-3 flex flex-col gap-1.5">
                            <div
                                v-for="(commission, type) in audit.commission_types"
                                :key="type"
                                class="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 dark:border-gray-700"
                            >
                                <div class="flex items-center gap-2">
                                    <span
                                        class="rounded px-1.5 py-0.5 text-xs font-medium"
                                        :class="getCommissionTypeColor(commission.type)"
                                    >
                                        {{ formatCommissionType(commission.type) }}
                                    </span>
                                    <span class="font-mono text-xs text-gray-500 dark:text-gray-400">
                                        {{ commissionShare(commission) }}%
                                    </span>
                                </div>
                                <span class="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                                    {{ $root.formatCurrency(commission.amount) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        Nenhuma informação de comissão disponível.
                    </div>
                </section>

                <section v-else-if="activeTab === 'receivables'" class="space-y-4">
                    <cashout-payables
                        :cashout="cashout"
                        :products="true"
                        :payable-details="payableDetails"
                    />
                </section>
            </div>
        </div>

        <!-- Decision form -->
        <div class="px-5 py-4">
            <form @submit.prevent="submit">
                <cards-radio-group
                    v-model="form.status"
                    :options="[
                        { label: 'Aprovado', value: 'approved' },
                        { label: 'Atenção', value: 'warning' },
                        { label: 'Reprovado', value: 'rejected' },
                    ]"
                />

                <label
                    for="content"
                    class="mb-1.5 mt-3 block text-xs font-medium text-gray-700 dark:text-gray-400"
                >
                    Descrição
                </label>
                <textarea
                    v-model="form.content"
                    id="content"
                    rows="3"
                    placeholder="Observações sobre o saque..."
                    class="block w-full rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400"
                ></textarea>
                <button
                    type="submit"
                    :disabled="loading"
                    class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
                    :class="submitButtonClass"
                >
                    <svg v-if="loading" class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Enviar</span>
                    <kbd
                        v-if="isMacOS"
                        class="rounded border px-1.5 py-0.5 font-mono text-[10px]"
                        :class="submitKbdClass"
                    >
                        &#8984; Enter
                    </kbd>
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import CardsRadioGroup from "../../forms/CardsRadioGroup.vue";
import { Store } from "../../../stores/store.js";
import { mapWritableState } from "pinia";
import InfoCol from "../../layouts/InfoCol.vue";
import SubmitButton from "../../forms/SubmitButton.vue";
import CashoutPayables from "../../../views/finnancial/cashout/CashoutPayables.vue";
import CashoutTags from "../CashoutTags.vue";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";
import CashoutStatus from "../../layouts/CashoutStatus.vue";
import CommissionType from "../../../mixins/CommissionType.vue";
import { ExclamationCircleIcon, EllipsisVerticalIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import SystemAnalysis from "./SystemAnalysis.vue";
import BinUsageCard from "./BinUsageCard.vue";
import PaymentMethodCard from "./PaymentMethodCard.vue";

export default {
    components: {
        CashoutStatus,
        ArrowTopRightOnSquareIcon,
        CashoutTags,
        CashoutPayables,
        SubmitButton,
        InfoCol,
        CardsRadioGroup,
        ExclamationCircleIcon,
        EllipsisVerticalIcon,
        XMarkIcon,
        Menu,
        MenuButton,
        MenuItems,
        MenuItem,
        SystemAnalysis,
        BinUsageCard,
        PaymentMethodCard,
    },
    mixins: [CommissionType],
    props: {
        cashout: {
            type: Object,
            required: true,
        },
        review: {
            type: Object,
            required: true,
        },
        payableDetails: {
            type: Object,
            default: null,
        },
    },
    emits: ["close", "fetch"],

    data() {
        return {
            loading: false,
            cashoutTagsCount: 0,
            isMacOS: navigator.platform.toUpperCase().indexOf("MAC") >= 0,
            activeTab: "system",
            showAdvanced: false,
            binSummary: "",
            binPriority: "normal",
            binBadge: null,
            paymentMethodSummary: "",
            paymentMethodPriority: "normal",
            paymentMethodBadge: null,
            systemAnalysisSummary: "",
            systemAnalysisPriority: "normal",
            systemAnalysisBadge: null,
            commissionTypesSummary: "",
            commissionTypesPriority: "normal",
            commissionTypesBadge: null,
            form: {
                status: "approved",
                content: "",
                cashout_id: this.cashout.id,
                user_id: "",
                balance: {
                    available: 0,
                    pending: 0,
                },
                commissions: {},
            },
        };
    },

    watch: {
        cashout() {
            if (this.cashout) {
                this.form.cashout_id = this.cashout.id;
                this.activeTab = "system";
                this.showAdvanced = false;
            }
        },
        audit: {
            immediate: true,
            handler(newAudit) {
                if (newAudit && newAudit.commission_types) {
                    const commissionTypesArray = Object.entries(
                        newAudit.commission_types,
                    ).map(([key, commission]) => ({
                        type: commission.type,
                        amount: commission.amount,
                        percentage:
                            (commission.amount / this.cashout.amount) * 100,
                    }));
                    this.updateCommissionTypesSummary(commissionTypesArray);
                }
            },
        },
    },

    computed: {
        ...mapWritableState(Store, ["authUser"]),
        audit() {
            const auditData = this.payableDetails
                ? this.payableDetails.audit
                : null;
            return auditData;
        },
        userAudits() {
            return this.payableDetails ? this.payableDetails.user_audits : null;
        },
        submitButtonClass() {
            const map = {
                approved: 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500',
                warning: 'bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500',
                rejected: 'bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500',
            };
            return map[this.form.status] || map.approved;
        },
        submitKbdClass() {
            const map = {
                approved: 'border-emerald-500/30 text-emerald-200',
                warning: 'border-amber-500/30 text-amber-200',
                rejected: 'border-red-500/30 text-red-200',
            };
            return map[this.form.status] || map.approved;
        },
        investigationTabs() {
            const priorityOrder = {
                danger: 3,
                warning: 2,
                normal: 1,
                none: 0,
            };

            const combinePriority = (...priorities) => {
                return priorities.reduce((current, candidate) => {
                    if (!candidate) {
                        return current;
                    }

                    return priorityOrder[candidate] > priorityOrder[current]
                        ? candidate
                        : current;
                }, "normal");
            };

            const receivablesCount = this.payableDetails?.payables
                ? this.payableDetails.payables.length
                : 0;

            return [
                {
                    id: "system",
                    label: "Sistema",
                    badge: this.systemAnalysisBadge,
                    priority: this.systemAnalysisPriority || "normal",
                },
                {
                    id: "payments",
                    label: "Pagamentos",
                    badge: this.binBadge || this.paymentMethodBadge,
                    priority: combinePriority(
                        this.binPriority || "normal",
                        this.paymentMethodPriority || "normal",
                    ),
                },
                {
                    id: "commissions",
                    label: "Comissões",
                    badge: this.commissionTypesBadge,
                    priority: this.commissionTypesPriority || "normal",
                },
                {
                    id: "receivables",
                    label: "Recebíveis",
                    badge: receivablesCount > 0 ? `${receivablesCount}` : null,
                    priority: receivablesCount > 0 ? "normal" : "none",
                },
            ];
        },
    },

    mounted() {
        this.form.user_id = this.cashout.user_id;
        this.form = this.review;
        if (this.form.status === "pending") {
            this.form.status = "approved";
        }

        document.addEventListener("keydown", this.handleKeyPress);
    },

    beforeUnmount() {
        document.removeEventListener("keydown", this.handleKeyPress);
    },

    methods: {
        toggleAdvanced() {
            this.showAdvanced = !this.showAdvanced;
            if (this.showAdvanced && !this.activeTab) {
                this.activeTab = "system";
            }
        },
        tabButtonClass(tab) {
            if (this.activeTab === tab.id) {
                return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400';
            }

            const emphasisMap = {
                danger: 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10',
                warning: 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10',
            };

            return emphasisMap[tab.priority] || 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700';
        },
        tabBadgeClass(priority) {
            const map = {
                danger: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400',
                warning: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
            };
            return map[priority] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
        },
        commissionShare(commission) {
            const baseAmount = this.cashout?.amount || 0;
            if (!baseAmount || !commission?.amount) {
                return "0.0";
            }

            const share = (commission.amount / baseAmount) * 100;
            return Number.isFinite(share) ? share.toFixed(1) : "0.0";
        },
        handleKeyPress(event) {
            if (this.isMacOS && event.metaKey && event.key === "Enter") {
                this.submit();
            }
        },
        submit() {
            this.loading = true;
            axios
                .put(
                    "/api/cashout/" +
                        this.cashout.id +
                        "/review/" +
                        this.review.id,
                    this.form,
                )
                .then(({ data }) => {
                    this.loading = false;
                    this.$emit("fetch");
                })
                .catch((error) => {
                    this.loading = false;
                });
        },
        fetchBalance() {
            this.loading = true;
            axios
                .get("/api/cashout/" + this.cashout.id + "/balance")
                .then(({ data }) => {
                    this.form.balance = {
                        available: data.event_properties.available_balance,
                        pending: data.event_properties.pending_balance,
                    };
                })
                .finally(() => (this.loading = false));
        },
        updateCommissions(event) {
            this.form.commissions = event.commissions;
        },
        fetchPayableDetails() {
            axios
                .get(`/api/cashout/${this.cashout.id}/payables/details`)
                .then(({ data }) => {
                    this.payableDetails = data;
                })
                .catch((error) => {
                    console.error(
                        "Erro ao buscar detalhes dos recebíveis:",
                        error,
                    );
                });
        },
        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = "Nenhum BIN encontrado";
                this.binPriority = "normal";
                this.binBadge = null;
                return;
            }

            const topBins = data.top_bins;
            const summary = data.summary;

            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = "danger";
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = "warning";
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                const highFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "high",
                ).length;
                const mediumFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "medium",
                ).length;
                const totalBins = summary?.total_unique_bins || topBins.length;

                if (highFraudBins > 0) {
                    this.binPriority = "danger";
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = "warning";
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = "normal";
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },
        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = "Nenhum método encontrado";
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (method) => method.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (method) => method.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Alta concentração";
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Concentração média";
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },
        updateSystemAnalysisSummary(analysis) {
            if (!analysis) {
                this.systemAnalysisSummary = "Análise não disponível";
                this.systemAnalysisPriority = "normal";
                this.systemAnalysisBadge = null;
                return;
            }

            if (analysis.analysis === "Reprovar" || analysis.confidence < 30) {
                this.systemAnalysisPriority = "danger";
            } else if (
                analysis.analysis === "Atenção" ||
                (analysis.confidence >= 30 && analysis.confidence < 70)
            ) {
                this.systemAnalysisPriority = "warning";
            } else {
                this.systemAnalysisPriority = "normal";
            }

            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },
        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary =
                    "Nenhum tipo de comissão encontrado";
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (commission) => commission.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (commission) => commission.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Alta concentração";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Concentração média";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },
    },
};
</script>
